import { useEffect, useMemo, type PropsWithChildren } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Navigate, useLocation } from "react-router-dom";
import { RedirectLogin } from "@/components/layout/RedirectLogin";
import { finishLogin, loginStartedFrom } from "@/lib/courseLogin";

export const ProtectedRoute = ({
  children,
  requireAuth = true,
}: PropsWithChildren<{ requireAuth?: boolean }>) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const location = useLocation();
  const currentPath = location.pathname + location.search + location.hash;
  const authStillStarting =
    inProgress === "startup" ||
    inProgress === "handleRedirect" ||
    inProgress === "logout";
  const signInNeeded = requireAuth && !isAuthenticated;
  // Decided once per page: this link started a sign-in and came back without
  // an account, so the visitor left the Microsoft screen with Back.
  const cameBackWithoutSigningIn = useMemo(
    () => signInNeeded && loginStartedFrom(currentPath),
    [signInNeeded, currentPath]
  );

  const forgetFinishedSignIn = () => {
    if (!requireAuth || !isAuthenticated) return;
    finishLogin();
  };
  useEffect(forgetFinishedSignIn, [requireAuth, isAuthenticated]);

  const forgetAbandonedSignIn = () => {
    if (!cameBackWithoutSigningIn) return;
    finishLogin();
  };
  useEffect(forgetAbandonedSignIn, [cameBackWithoutSigningIn]);

  if (authStillStarting) return null;
  if (!signInNeeded) return <>{children}</>;
  if (cameBackWithoutSigningIn) return <Navigate to="/impressum" replace />;
  return <RedirectLogin returnTo={currentPath} />;
};
